// pages/TipScreen.js
// Customer tip screen — shown after pickup completion.
// Original IP — LoadDash Services LLC

import React, { useState } from "react";
import { BRAND_COLORS } from "../constants";

const BC = BRAND_COLORS;

const TIP_OPTIONS = [
  { amount: 5, label: "Nice" },
  { amount: 10, label: "Great" },
  { amount: 15, label: "Best" },
  { amount: null, label: "Custom" },
];

export default function TipScreen({ loaderName, loaderInitials, jobTotal, onTipSent, onSkip }) {
  const [selectedTip, setSelectedTip] = useState(10);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [sending, setSending] = useState(false);

  const tipValue = isCustom ? (parseFloat(customAmount) || 0) : selectedTip;

  function handleSelect(opt) {
    if (opt.amount === null) {
      setIsCustom(true);
      setSelectedTip(0);
    } else {
      setIsCustom(false);
      setSelectedTip(opt.amount);
      setCustomAmount("");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: BC.GRAY, fontFamily: "'Sora', sans-serif" }}>

      {/* ── Dark header ────────────────────────────── */}
      <div style={{ background: BC.NAVY, padding: "calc(env(safe-area-inset-top, 16px) + 24px) 20px 28px", textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: BC.BLUE, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={BC.WHITE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: BC.WHITE, marginBottom: 4 }}>Pickup complete</div>
        <div style={{ fontSize: 13, color: BC.BLUE, fontWeight: 500 }}>Your items have been picked up</div>
      </div>

      {/* ── Content area ───────────────────────────── */}
      <div style={{ padding: "20px 16px calc(24px + env(safe-area-inset-bottom, 0px))" }}>

        {/* Hauler avatar card */}
        <div style={{ background: BC.WHITE, borderRadius: 14, padding: "16px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: BC.NAVY, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: BC.WHITE, letterSpacing: 0.5 }}>{loaderInitials}</span>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: BC.NAVY }}>{loaderName}</div>
            <div style={{ fontSize: 12, color: BC.BLUE, fontWeight: 500, marginTop: 1 }}>Your hauler today</div>
          </div>
        </div>

        {/* Tip label */}
        <div style={{ fontSize: 15, fontWeight: 700, color: BC.NAVY, marginBottom: 14, textAlign: "center" }}>
          Leave a tip? <span style={{ color: BC.BLUE }}>100% goes to your hauler.</span>
        </div>

        {/* Tip buttons */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          {TIP_OPTIONS.map((opt) => {
            const active = opt.amount === null ? isCustom : (!isCustom && selectedTip === opt.amount);
            return (
              <button
                key={opt.label}
                onClick={() => handleSelect(opt)}
                style={{
                  padding: "14px 4px",
                  borderRadius: 12,
                  border: `1.5px solid ${active ? BC.BLUE : BC.GRAY}`,
                  background: active ? `${BC.BLUE}14` : BC.WHITE,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  minHeight: 44,
                  minWidth: 44,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800, color: active ? BC.BLUE : BC.NAVY }}>
                  {opt.amount !== null ? `$${opt.amount}` : "Other"}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: BC.BLUE, marginTop: 2 }}>
                  {opt.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Custom input */}
        {isCustom && (
          <div style={{ background: BC.WHITE, borderRadius: 12, border: `1.5px solid ${BC.BLUE}`, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: BC.NAVY }}>$</span>
            <input
              value={customAmount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, "");
                setCustomAmount(val);
              }}
              placeholder="0.00"
              type="text"
              inputMode="decimal"
              autoFocus
              style={{
                flex: 1,
                fontSize: 20,
                fontWeight: 700,
                color: BC.NAVY,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: "inherit",
                padding: 0,
              }}
            />
          </div>
        )}

        {/* Note */}
        <div style={{ fontSize: 11, color: BC.BLUE, textAlign: "center", marginBottom: 20, fontWeight: 500 }}>
          LoadDash does not take a cut of tips.
        </div>

        {/* Totals */}
        <div style={{ background: BC.WHITE, borderRadius: 14, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: `1px solid ${BC.GRAY}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: BC.NAVY }}>Job total</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: BC.NAVY }}>${(jobTotal || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: BC.NAVY }}>Tip</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: BC.ORANGE }}>${tipValue.toFixed(2)}</span>
          </div>
        </div>

        {/* CTA — label shows the TIP amount only, never job total + tip */}
        <button
          onClick={async () => {
            if (!onTipSent || tipValue <= 0 || sending) return;
            setSending(true);
            try { await onTipSent(tipValue); } finally { setSending(false); }
          }}
          disabled={tipValue <= 0 || sending}
          style={{
            width: "100%",
            padding: 16,
            borderRadius: 12,
            border: "none",
            background: tipValue > 0 && !sending ? BC.BLUE : BC.GRAY,
            color: tipValue > 0 && !sending ? BC.WHITE : "#aaa",
            fontWeight: 800,
            fontSize: 15,
            cursor: tipValue > 0 && !sending ? "pointer" : "not-allowed",
            fontFamily: "inherit",
            boxShadow: tipValue > 0 && !sending ? `0 4px 16px ${BC.BLUE}44` : "none",
            marginBottom: 10,
            minHeight: 44,
          }}
        >
          {sending ? "Opening secure checkout…" : `Send $${tipValue.toFixed(2)} tip`}
        </button>

        {/* Skip */}
        <button
          onClick={() => onSkip && onSkip()}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 12,
            border: "none",
            background: "transparent",
            color: BC.BLUE,
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
            minHeight: 44,
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
